import {
  Box,
  Button,
  HStack,
  IconButton,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Controller, useFormContext } from "react-hook-form";
import { AdSetFormData } from "../forms/adset";
import { useCallback, useState } from "react";
import { Select } from "chakra-react-select";
import WhiteBgInput from "../../../Guestlist/WhiteBgInput";
import { StepProps } from "../types";
import { FaMinus, FaPlus } from "react-icons/fa";
import IconWrapper from "./IconWrapper";

const labelForType = (type: string) => {
  switch (type) {
    case "countries":
      return "Countries";
    case "interests":
      return "Interests";
    case "behaviors":
      return "Behaviors";
    case "demographics":
      return "Demographics";
    case "geo_markets":
      return "Location";
    case "adeducationschool":
      return "Education";
    case "adworkemployer":
      return "Employer";
    case "adworkposition":
      return "Position";
    default:
      return "Targeting";
  }
};

const EditLocationsWidget = ({
  isLoading = false,
  search,
}: {
  isLoading?: boolean;
  search?: StepProps["search"];
}) => {
  const { control, setValue, register, watch, getValues } =
    useFormContext<AdSetFormData>();
  const [targetingSearchResults, setTargetingSearchResults] = useState<{
    type: "geo_markets";
    results: {
      id: string;
      name: string;
      value: string;
      key: string;
      country_code?: string;
      country_name?: string;
    }[];
  }>({
    type: "geo_markets",
    results: [],
  });

  const targetingType = "geo_markets";
  const searchTargetingType = useCallback(
    (query: string, subtype: string) => {
      return search[targetingType](query).then((data) => {
        setTargetingSearchResults({
          type: targetingType,
          results: data.data.map((r) => ({
            ...r,
            name: `${r.name} (${r.country_name || r.country_code})`,
            value: `${r.name} (${r.country_name || r.country_code})`,
            country: r.country_code,
            key: r.key,
            id: r.key,
          })),
        });
      });
    },
    [search]
  );

  const searchTargeting = useCallback(
    ({ query, subtype }: { query: string; subtype: string }) => {
      searchTargetingType(query, subtype);
    },
    [targetingType, watch]
  );

  const targetingOptions = [
    ...(watch("geo_markets") || []).map((g) => ({
      type: "geo_markets",
      value: g.key || g.value,
      country: g.country,
      name: g.name || g.value,
    })),
    ...(watch("countries") || []).map((c) => ({
      type: "countries",
      value: c.key || c.value,
      country: c.key,
      name: c.value,
    })),
  ];

  const isSelected = useCallback(
    (value: { key: string; name: string }) => {
      return targetingOptions.some((t: any) => t.value === value.key);
    },
    [targetingOptions]
  );

  const removeTargeting = useCallback(
    (targeting: {
      type: keyof typeof search | "";
      value?: string;
      key?: string;
    }) => {
      const options: { value: string; key?: string; id?: string }[] = getValues(
        targeting.type as any
      );
      console.log({
        targeting,
        options,
      });

      const formattedTargeting = {
        key: targeting.key || targeting.value,
        type: targeting.type,
      };

      setValue(
        targeting.type as any,
        options.filter((o) => o.key !== formattedTargeting.key)
      );
    },
    [setValue, getValues]
  );

  const addTargeting = useCallback(
    (targeting: {
      name: string;
      type: keyof typeof search | "";
      country?: string;
      value?: string;
      key?: string;
    }) => {
      const options = watch(targetingType);
      setValue(targetingType, [...options, targeting]);
    },
    [setValue, watch]
  );

  return (
    <VStack w="full">
      <HStack w="full" justifyContent="space-between">
        <WhiteBgInput
          placeholder="Search"
          {...register(`targeting_query`)}
          isDisabled={isLoading}
          flexGrow={1}
          w="full"
        />
        <Button
          flexShrink={0}
          isLoading={isLoading}
          onClick={() =>
            searchTargeting({
              query: watch("targeting_query"),
              subtype: watch("targeting_subtype")?.value,
            })
          }
        >
          Search
        </Button>
      </HStack>
      <VStack w="full" px={4} maxH="500px" overflowY="scroll">
        {targetingSearchResults.results.map((t) => (
          <HStack w="full" justifyContent="space-between" key={t.id}>
            <Text>{t.name}</Text>
            <IconButton
              variant="ghost"
              isLoading={isLoading}
              isDisabled={isLoading}
              onClick={() =>
                isSelected(t)
                  ? removeTargeting({
                      ...t,
                      type: targetingSearchResults.type,
                    })
                  : addTargeting({
                      ...t,
                      type: targetingSearchResults.type,
                      value: t.key,
                      key: t.key,
                      name: t.name,
                    })
              }
              aria-label="Ad targeting"
              icon={isSelected(t) ? <FaMinus /> : <FaPlus />}
            />
          </HStack>
        ))}
      </VStack>
      <Box w="full">
        <HStack
          w="full"
          gap={1}
          justifyContent={"flex-start"}
          flexWrap={"wrap"}
        >
          {targetingOptions.map((t) => (
            <IconWrapper
              item={t}
              isLoading={isLoading}
              onRemove={removeTargeting}
            />
          ))}
        </HStack>
      </Box>
    </VStack>
  );
};

export default EditLocationsWidget;
